import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Box, MuiBox, FlexBox, Grid, InputField, Typography, color } from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { actions as authActions, selectors as authSelectors } from 'store/auth';
import { actions as clientsActions, selectors as clientsSelectors } from 'store/clients';
import { validateEmail } from 'utils/validation';
import { TextButton } from 'components/TextButton';
import { KioskControlCenter } from 'components/KioskControlCenter';
import { useStyles } from './styled';

const INVALID_EMAIL = 'Invalid email format';

const SignInView: React.FC = () => {
  // ACTION CREATORS AND SELECTORS
  const navigate = useNavigate();

  const { login, resetError, getClients, resetClients, setClientId } = useActions({
    login: authActions.login,
    resetError: authActions.resetError,
    getClients: clientsActions.getClients,
    resetClients: clientsActions.resetClients,
    setClientId: clientsActions.setClientId
  });
  const admin = useSelector(authSelectors.selectAdmin);
  const authenticating = useSelector(authSelectors.selectAuthenticating);
  const error = useSelector(authSelectors.selectError);
  const clientId = useSelector(clientsSelectors.selectClientId);
  const clients = useSelector(clientsSelectors.selectClients);

  const classes = useStyles();

  // EMAIL AND PASSWORD INPUT STATES
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // INVALID MESSAGE WHEN EMAIL INCORRECT FORMAT
  const [invalidMsg, setInvalidMsg] = useState<string | null>(null);

  // ON COMPONENT MOUNT: RESET CLIENTS IN STORE
  useEffect(() => {
    resetClients();
  }, []);

  // ON UPDATE OF ERROR - SET THE ERROR AS INVALID MESSAGE
  useEffect(() => {
    setInvalidMsg(error);
  }, [error]);

  // SET CLIENT ID WHEN ADMIN CHANGES
  useEffect(() => {
    setClientId(admin?.clientId || '');
  }, [admin]);

  // FETCH CLIENTS WHEN CLIENT ID CHANGES
  useEffect(() => {
    if (admin && clientId) getClients();
  }, [clientId]);

  // REDIRECT BASED ON ADMIN ROLE WHEN THE CLIENT IS KNOWN
  useEffect(() => {
    if (!admin || !clientId || !clients.length) return;

    if (admin.resetPasswordRequired) {
      navigate('/resetpassword');
    } else if (admin.role === 'GlobalAdmin') {
      navigate('/customers');
    } else {
      navigate('/kiosks');
    }
  }, [clientId, clients]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // EMAIL VALIDATION
  const emailValidationFn = async (value: string | number | undefined) => {
    // VALIDATION SCHEMA
    const schema = validateEmail(INVALID_EMAIL);

    // VALIDATE FN
    const result = await schema.validate(value).catch((err) => err);

    // IF THERE WAS ERROR MESSAGE - SET IT TO INVALID MESSAGE
    setInvalidMsg(result && result.message);
  };

  // INPUT CHANGE
  const onInputChange = (setFn: React.Dispatch<React.SetStateAction<string>>, value: string) => {
    // IF INVALID MESSAGE EXISTS SET IT TO NULL
    if (invalidMsg) setInvalidMsg(null);
    // RESET ERROR TO INITIAL STATE
    resetError();
    // FIRES THE APPROPRIATE FN: SETEMAIL | SETPASSWORD WITH VALUE
    setFn(value);
  };

  // FORM SUBMIT
  const onLoginClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // FIRES LOGIN ASYNC FN
    login({ email, password });
  };

  const buttonDisabled = !email.length || !password.length || !!invalidMsg;

  return (
    <Grid container direction="column" className={classes.signinViewContainer}>
      {/* LEFT OF CARD */}
      <Grid item md={12} xl={6} lg={6} className={classes.signinViewImageBlocks}>
        <FlexBox fullHeight bg="light-gray" sx={{ minHeight: '100%' }}>
          <KioskControlCenter />
        </FlexBox>
      </Grid>
      {/* RIGHT OF CARD - LOGIN FORM */}
      <Grid item md={12} xl={6} lg={6} className={classes.signinViewBlocks}>
        <MuiBox className={classes.signinHeaderBox}>
          <Typography className={classes.signinHeading}>Client Admin Sign In</Typography>
        </MuiBox>
        <form>
          {/* EMAIL INPUT */}
          <MuiBox className={classes.signinEmail}>
            <InputField
              onChange={(e) => onInputChange(setEmail, e.target.value)}
              error={!!invalidMsg}
              helperText={emailValidationFn}
              label="Email"
              type="email"
              placeholder="Your Email"
              fullWidth
            />
          </MuiBox>
          {/* PASSWORD INPUT */}
          <MuiBox className={classes.signinPassword}>
            <InputField
              onChange={(e) => onInputChange(setPassword, e.target.value)}
              error={!!invalidMsg && invalidMsg !== INVALID_EMAIL}
              label="Password"
              type="password"
              placeholder="Your Password"
              fullWidth
            />
          </MuiBox>
          <MuiBox className={classes.signinForgotPassword}>
            <TextButton type="button" onClick={() => navigate('/forgotpassword')}>
              Forgot your password?
            </TextButton>
          </MuiBox>
          {/* ERROR MESSAGE */}
          <Box flexOverride="none" margin="0 0 5" height={24}>
            {invalidMsg && !authenticating && (
              <Typography fontSize={16} color={color.RED}>
                {invalidMsg}
              </Typography>
            )}
          </Box>
          {/* SUBMIT BUTTON */}
          <Button
            disabled={buttonDisabled}
            customSize="large"
            onClick={onLoginClick}
            fullWidth
            isLoading={authenticating ? true : false}
            type="submit"
            className={classes.signinButton}
          >
            Sign In
          </Button>
        </form>
      </Grid>
    </Grid>
  );
};

export default SignInView;
